import React from 'react';
import { useTranslation } from 'react-i18next';
import type { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import { useAppDispatch, useAppSelector } from '@/store';
import { trackAddPaymentInfo, trackPurchase, trackSubscribe, trackOnce } from '@/utils/analytics';
import { pulsarEvent } from '@/utils/analytics/pulsar';
import { sessionSave } from '@/store/slices/session';
import { isEmailValid } from '@/utils/strings';
import { stripeLocale } from '@/utils/stripe-locale';
import { useThemeMode } from '@/nova/theme/OwsTheme';
import { asset } from '../../../../lib/asset';
import { readStartVoucherCode } from '../../../../lib/startProperties';
import {
  getStripe,
  isStripeLive,
  reconcileSessionInvoice,
  useStripeAppearance,
} from '../../../../data/payments';
import { PREVIEW_LOADER_SRC } from '../../../../components/wizard/PreviewPlaceholder';
import { FINISH } from '../../finish.tokens';
import { PaymentChrome } from './PaymentChrome';
import { CheckoutError } from './CheckoutError';
import {
  Card,
  SmallLabel,
  SmallLabelTop,
  ExpressGrid,
  ExpressLink,
  ExpressAmazon,
  LinkIcon,
  AmazonIcon,
  MethodList,
  MethodRow as MethodRowEl,
  MethodIcon,
  MethodIconImg,
  MethodLabel,
  DemoNote,
  LiveExtra,
  Spinner,
  StatusBox,
  StatusText,
} from './PaymentCheckout.styles';

export type CheckoutProps = {
  email: string;
  onEmail: (value: string) => void;
  /** Net amount actually charged/displayed today (voucher already subtracted). */
  amount: number;
  /** GROSS order total, before any voucher. Conversion analytics report this (like the other
   *  events), independent of the discounted `amount` the buyer is charged. */
  grossAmount: number;
  /** Monthly recurring (product-features) total, for the Subscribe conversion. */
  monthlyAmount: number;
  currency: string;
  currencySymbol: string;
  totalLabel: string;
  totalSub?: string;
  payLabel: string;
  onSuccess: () => void;
};

/* ----------------------------- demo placeholders ---------------------------- */

function MethodRow({ icon, label }: { icon: React.ReactNode; label: string }): React.ReactElement {
  return (
    <MethodRowEl>
      <MethodIcon>{icon}</MethodIcon>
      <MethodLabel text={label} tag="span" />
    </MethodRowEl>
  );
}

function DemoMethods(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <Card>
      <SmallLabel text="Express checkout" tag="paragraph" />
      <ExpressGrid>
        <ExpressLink>
          <LinkIcon src={asset('payment/link.png')} alt={t('Link')} />
        </ExpressLink>
        <ExpressAmazon>
          <AmazonIcon src={asset('payment/amazonpay.png')} alt={t('Amazon Pay')} />
        </ExpressAmazon>
      </ExpressGrid>

      <SmallLabelTop text="Other payment methods" tag="paragraph" />
      <MethodList>
        <MethodRow
          label={t('Card')}
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
              <rect
                x="2.5"
                y="5"
                width="19"
                height="14"
                rx="2.5"
                stroke={FINISH.methodIconStroke}
                strokeWidth="1.6"
              />
              <path d="M2.5 9.5h19" stroke={FINISH.methodIconStroke} strokeWidth="1.6" />
            </svg>
          }
        />
        <MethodRow
          label="Alipay"
          icon={<MethodIconImg src={asset('payment/alipay.svg')} alt="" />}
        />
        <MethodRow
          label="WeChat Pay"
          icon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill={FINISH.weChatGreen} aria-hidden>
              <circle cx="12" cy="12" r="10" opacity="0.18" />
              <circle cx="9" cy="10.5" r="1.1" />
              <circle cx="15" cy="10.5" r="1.1" />
              <path
                d="M5 11.5a4.7 4.7 0 0 1 9.3 0M18.8 16.2a3.6 3.6 0 0 0 .5-6.6"
                stroke={FINISH.weChatGreen}
                strokeWidth="1.4"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
          }
        />
      </MethodList>

      <DemoNote text="Demo mode: no real payment is taken." tag="paragraph" />
    </Card>
  );
}

function DemoCheckout(p: CheckoutProps): React.ReactElement {
  const [paying, setPaying] = React.useState(false);
  const payTimerRef = React.useRef<number | null>(null);
  // Cancel the pending "success" if the pane unmounts (e.g. the user clicks Back).
  React.useEffect(
    () => () => {
      if (payTimerRef.current != null) window.clearTimeout(payTimerRef.current);
    },
    [],
  );
  const pay = (): void => {
    setPaying(true);
    payTimerRef.current = window.setTimeout(() => {
      payTimerRef.current = null;
      p.onSuccess();
    }, 900);
  };
  return (
    <PaymentChrome
      email={p.email}
      onEmail={p.onEmail}
      totalLabel={p.totalLabel}
      totalSub={p.totalSub}
      amount={p.amount}
      currencySymbol={p.currencySymbol}
      currencyCode={p.currency}
      payLabel={p.payLabel}
      paying={paying}
      onPay={pay}
    >
      <DemoMethods />
    </PaymentChrome>
  );
}

/* ------------------------------- live (Stripe) ------------------------------ */

function LiveForm(p: CheckoutProps & { clientSecret: string }): React.ReactElement {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Conversion: AddPaymentInfo / add_payment_info fires once when valid payment details are
  // submitted (before the charge is confirmed). No-ops without marketing consent.
  const paymentInfoTrackedRef = React.useRef(false);

  // Confirm the payment, then REPORT it: `onSuccess` acknowledges the payment with the backend
  // (`POST /session/payment-acknowledged`) and swaps in the confirmation.
  //
  // This used to WAIT instead, on the assumption that the backend would advance the session by
  // itself once the charge settled and the payment step's `session.status` watcher would pick it
  // up. It no longer does: the transition has to be requested, so a clean confirm left the button
  // stuck on "Processing…" forever and the paid session stranded in `configuration`. The status
  // watcher stays as a backstop for a session the backend does still push (e.g. a 3DS return
  // restored by `sessionLoad`).
  //
  // Only an ERROR releases the button; on a clean confirm it stays disabled until the screen
  // swaps, so the buyer can't submit twice. `expressEvent` is present for the wallet
  // (Apple/Google Pay) path — on any failure we must call `paymentFailed()` so the native sheet
  // dismisses (v2 parity).
  const confirm = async (expressEvent?: StripeExpressCheckoutElementConfirmEvent): Promise<void> => {
    if (!stripe || !elements) {
      expressEvent?.paymentFailed({ reason: 'fail' });
      return;
    }
    setPaying(true);
    setError(null);
    const fail = (message: string | undefined): void => {
      setError(message ?? t('Payment failed. Please try again.'));
      setPaying(false);
      expressEvent?.paymentFailed({ reason: 'fail' });
    };
    // Validate + collect the element first, then confirm — matching v2's flow.
    const submitResult = await elements.submit();
    if (submitResult.error) {
      fail(submitResult.error.message);
      return;
    }
    if (!paymentInfoTrackedRef.current) {
      paymentInfoTrackedRef.current = true;
      // Report the GROSS order value (matching the other conversion events), not the
      // voucher-discounted amount the buyer is charged.
      trackAddPaymentInfo({ value: p.grossAmount, currency: p.currency });
      pulsarEvent('add_payment_info', { value: p.grossAmount, currency: p.currency });
    }
    const result = await stripe.confirmPayment({
      elements,
      clientSecret: p.clientSecret,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.origin + window.location.pathname },
    });
    if (result.error) {
      fail(result.error.message);
      return;
    }
    // A declined card comes back as `result.error` above, so reaching here means Stripe accepted
    // the payment: `succeeded` for a card, or `processing` for a method that settles
    // asynchronously. Both are "the buyer has paid", which is what the acknowledge reports; a
    // charge that later fails moves the session to `payment_failed` and the retry banner picks it
    // up. A redirect-based method never reaches this line at all (the page navigates away and
    // comes back to `return_url`, where the entry probe finds the paid invoice instead).
    p.onSuccess();
  };

  return (
    <PaymentChrome
      email={p.email}
      onEmail={p.onEmail}
      totalLabel={p.totalLabel}
      totalSub={p.totalSub}
      amount={p.amount}
      currencySymbol={p.currencySymbol}
      currencyCode={p.currency}
      payLabel={p.payLabel}
      paying={paying}
      error={error}
      onPay={() => void confirm()}
    >
      <ExpressCheckoutElement onConfirm={(e) => void confirm(e)} />
      <LiveExtra>
        <PaymentElement options={{ defaultValues: { billingDetails: { email: p.email } } }} />
      </LiveExtra>
    </PaymentChrome>
  );
}

function LiveCheckout(p: CheckoutProps): React.ReactElement {
  // The invoice (and thus the clientSecret) is created against the live session — the
  // backend derives the amount from it AND reads the buyer's email OFF the session,
  // mirroring v2's `/invoice { session_id }`. So before requesting it we GUARANTEE the
  // session carries the buyer's email: a prior autosave may have dropped it (the email
  // association is volatile), and an email-less session makes `/invoice` 500 with
  // SESSION_NO_EMAIL. We persist + await the email, then create the invoice against it.
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector((s) => s.session.sessionId);
  const sessionData = useAppSelector((s) => s.session.data);
  const authEmail = useAppSelector((s) => s.authentication.user?.email);
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [error, setError] = React.useState<'start' | 'email' | null>(null);
  const [retryNonce, setRetryNonce] = React.useState(0);

  // Best-known buyer email: the live checkout field, else whatever the session / auth carry.
  const email = (p.email || sessionData.configuration_data?.emailAddress || authEmail || '').trim();
  // Read the latest session body inside the (one-shot) start so the save never persists
  // stale data and the effect doesn't re-run on unrelated session changes.
  const dataRef = React.useRef(sessionData);
  dataRef.current = sessionData;
  // Guards the start to run at most once per session; a later-arriving email recovers it.
  const startedRef = React.useRef(false);

  // "Try again" from the error screen: reset the one-shot guard + bump the nonce so the
  // effect re-runs and re-attempts the start.
  const reload = React.useCallback(() => {
    startedRef.current = false;
    setError(null);
    setClientSecret(null);
    setRetryNonce((n) => n + 1);
  }, []);

  React.useEffect(() => {
    if (!sessionId || startedRef.current) return;
    if (!isEmailValid(email)) {
      setError('email');
      return;
    }
    startedRef.current = true;
    setError(null);
    let cancelled = false;
    // Persist the buyer email (and the effective voucher, below) onto the session BEFORE creating
    // the invoice — `/invoice` reads the email off the session body and 500s (SESSION_NO_EMAIL)
    // without it.
    (async () => {
      try {
        // The marketing-site hand-off voucher (a standalone `?voucher_code=` URL param, or the one
        // embedded in `startProperties`) SUPERSEDES the voucher already on the session — mirroring
        // buildConfigurationData — so an abandoned-cart owner who DEEP-LINKS straight onto payment,
        // with no intervening step-save to persist the override, still checks out on the NEW code
        // rather than the stale session one. Falls back to the session's voucher when there is none.
        const effectiveVoucher = readStartVoucherCode() ?? dataRef.current.configuration_data?.voucher_code ?? null;
        const nextConfig = {
          ...dataRef.current.configuration_data,
          emailAddress: email,
          ...(effectiveVoucher ? { voucher_code: effectiveVoucher } : {}),
        };
        await dispatch(sessionSave({ id: sessionId, data: { ...dataRef.current, configuration_data: nextConfig } })).unwrap();
        if (cancelled) return;
        // Guard against a STALE invoice before loading Stripe: `POST /invoice` is idempotent per
        // session, so a buyer who went Back, switched campaign / edited features — or whose voucher
        // was just overridden above — would otherwise pay the original invoice's frozen amount.
        // `reconcileSessionInvoice` compares the pending invoice's snapshot to the live selection
        // (voucher included) and cancels + reissues on a mismatch. The effective voucher is passed
        // through so the reissued invoice redeems it.
        const outcome = await reconcileSessionInvoice(sessionId, nextConfig, effectiveVoucher);
        if (cancelled) return;
        // A voucher can cover the WHOLE bill: the backend returns an already-paid invoice, so there
        // is nothing to charge. Skip Stripe entirely and jump straight to the success screen. With no
        // Stripe charge there is no payment webhook, so the session-status-gated Purchase conversion
        // may never fire for a free order; fire it here on the GROSS total, deduped with the SAME
        // per-session key the status-gated effect uses so it still counts exactly once either way.
        if (outcome.paid) {
          if (sessionId) {
            trackOnce(`${sessionId}:purchase`, () => {
              trackPurchase({ transactionId: sessionId, value: p.grossAmount, currency: p.currency, contentIds: [sessionId] });
              pulsarEvent('purchase', { transaction_id: sessionId, value: p.grossAmount, currency: p.currency });
              if (p.monthlyAmount > 0) {
                trackSubscribe({ value: p.monthlyAmount, currency: p.currency });
                pulsarEvent('subscribe', { value: p.monthlyAmount, currency: p.currency });
              }
            });
          }
          p.onSuccess();
          return;
        }
        setClientSecret(outcome.clientSecret);
      } catch {
        if (!cancelled) {
          startedRef.current = false;
          setError('start');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, email, retryNonce]);

  // Stripe Elements appearance, resolved to the active studio theme (re-themes
  // live on a light⇄dark switch). Called before the early returns (rules-of-hooks).
  const stripeAppearance = useStripeAppearance();
  // Stripe renders its OWN copy (field labels, validation errors, the country list) and defaults
  // to `auto`, which reads the BROWSER's language rather than ours — so an owner running an
  // English browser saw an English card form inside a translated checkout. Same hook placement
  // rule: before the early returns.
  const { i18n } = useTranslation();
  const locale = stripeLocale(i18n.language);
  // Studio theme: the loader art is white on the dark pane, darkened on the light one.
  const themeMode = useThemeMode();

  // No session / no usable email → can't proceed; the buyer goes back to fix it.
  if (!sessionId || error === 'email') return <CheckoutError noSession />;
  // The invoice / Stripe start failed — recoverable, so offer a retry.
  if (error === 'start') return <CheckoutError onRetry={reload} />;
  if (!clientSecret) {
    return (
      <StatusBox>
        <Spinner $onLight={themeMode === 'light'} src={PREVIEW_LOADER_SRC} alt="" />
        <StatusText text="Starting secure checkout…" tag="paragraph" />
      </StatusBox>
    );
  }
  return (
    <Elements
      // `locale` and `clientSecret` are NOT mutable after mount — unlike `appearance`, which
      // Stripe applies in place. Keying on the locale remounts Elements on a language switch
      // instead of leaving it stuck in the language the checkout opened in.
      key={locale}
      stripe={getStripe()}
      options={{ clientSecret, appearance: stripeAppearance, locale }}
    >
      <LiveForm {...p} clientSecret={clientSecret} />
    </Elements>
  );
}

/**
 * The checkout body. Decides between LIVE (real Stripe Elements — used whenever a
 * publishable key is configured on `window.config`) and DEMO (static payment UI +
 * a simulated success), keyed off `isStripeLive()` at runtime.
 */
export const PaymentCheckout = (props: CheckoutProps): React.ReactElement =>
  isStripeLive() ? <LiveCheckout {...props} /> : <DemoCheckout {...props} />;

export default PaymentCheckout;
